import { create } from 'zustand';
import {
  getCurrentUserRequest,
  getSelectContextOptionsRequest,
  loginRequest,
  logoutRequest,
  selectContextRequest,
  startSelectContextRequest,
} from '@/services/modules/auth';
import type {
  CurrentUser,
  LoginCompanyOption,
  LoginPayload,
  LoginResult,
  SelectContextPayload,
  SessionPayload,
  TokenPayload,
  UserContext,
} from '@/types/auth';
import {
  clearStoredTokens,
  getStoredAccessToken,
  getStoredExpiresAt,
  getStoredRefreshToken,
  getStoredTempToken,
  getStoredUserContext,
  setStoredTempToken,
  setStoredTokens,
  setStoredUserContext,
} from '@/utils/token';

interface AuthState {
  hydrated: boolean;
  accessToken: string;
  refreshToken: string;
  tempToken: string;
  expiresAt?: number;
  currentUser?: CurrentUser;
  userContext?: UserContext;
  roles: string[];
  permissions: string[];
  hydrate: () => void;
  setTokens: (payload: TokenPayload) => void;
  setSession: (payload: SessionPayload) => void;
  setPendingSelection: (tempToken: string) => void;
  setUserContext: (payload?: UserContext) => void;
  setCurrentUser: (currentUser?: CurrentUser) => void;
  bootstrap: () => Promise<CurrentUser | undefined>;
  login: (payload: LoginPayload) => Promise<LoginResult>;
  startSelectContextSwitch: () => Promise<LoginCompanyOption[]>;
  ensureSelectContextTempToken: () => Promise<string>;
  loadSelectContextOptions: () => Promise<LoginCompanyOption[]>;
  selectLoginContext: (
    payload: SelectContextPayload,
  ) => Promise<CurrentUser | undefined>;
  logout: () => Promise<void>;
  clearSessionState: () => void;
}

const resetState = {
  hydrated: false,
  accessToken: '',
  refreshToken: '',
  tempToken: '',
  expiresAt: undefined,
  currentUser: undefined,
  userContext: undefined,
  roles: [],
  permissions: [],
};

const mergeCurrentUserWithContext = (
  currentUser?: CurrentUser,
  userContext?: UserContext,
) => {
  if (!currentUser) {
    return undefined;
  }

  return {
    ...currentUser,
    accountId: currentUser.accountId ?? userContext?.accountId,
    customerUserId: currentUser.customerUserId ?? userContext?.customerUserId,
    companyId: currentUser.companyId ?? userContext?.companyId,
    companyName: currentUser.companyName || userContext?.companyName,
    shopId: currentUser.shopId ?? userContext?.shopId,
    shopName: currentUser.shopName || userContext?.shopName,
  };
};

export const useAuthStore = create<AuthState>((set, get) => ({
  ...resetState,
  hydrate: () => {
    if (get().hydrated) {
      return;
    }

    // 先从本地恢复 accessToken、refreshToken、临时上下文选择态。
    set({
      hydrated: true,
      accessToken: getStoredAccessToken(),
      refreshToken: getStoredRefreshToken(),
      tempToken: getStoredTempToken(),
      expiresAt: getStoredExpiresAt(),
      userContext: getStoredUserContext(),
    });
  },
  setTokens: (payload) => {
    const nextRefreshToken = payload.refreshToken ?? get().refreshToken ?? '';
    setStoredTokens({
      ...payload,
      refreshToken: nextRefreshToken || undefined,
    });
    set({
      accessToken: payload.accessToken,
      refreshToken: nextRefreshToken,
      expiresAt: payload.expiresAt,
      hydrated: true,
    });
  },
  setSession: (payload) => {
    // 一旦拿到正式 session，就清空 tempToken，并把组织/店铺上下文落盘。
    setStoredTokens(payload);
    setStoredTempToken();
    setStoredUserContext(payload.userContext);
    const nextCurrentUser = mergeCurrentUserWithContext(
      payload.currentUser,
      payload.userContext,
    );
    set({
      accessToken: payload.accessToken,
      refreshToken: payload.refreshToken ?? '',
      tempToken: '',
      expiresAt: payload.expiresAt,
      hydrated: true,
      userContext: payload.userContext,
      currentUser: nextCurrentUser,
      roles: nextCurrentUser?.roles ?? [],
      permissions: nextCurrentUser?.permissions ?? [],
    });
  },
  setPendingSelection: (tempToken) => {
    // 多组织账号第一次登录可能先拿到 tempToken，等待用户选择上下文。
    clearStoredTokens();
    setStoredTempToken(tempToken);
    set({
      ...resetState,
      hydrated: true,
      tempToken,
    });
  },
  setUserContext: (payload) => {
    setStoredUserContext(payload);
    const nextCurrentUser = mergeCurrentUserWithContext(
      get().currentUser,
      payload,
    );
    set({
      userContext: payload,
      currentUser: nextCurrentUser,
      roles: nextCurrentUser?.roles ?? [],
      permissions: nextCurrentUser?.permissions ?? [],
    });
  },
  setCurrentUser: (currentUser) => {
    const nextCurrentUser = mergeCurrentUserWithContext(
      currentUser,
      get().userContext,
    );
    set({
      currentUser: nextCurrentUser,
      roles: nextCurrentUser?.roles ?? [],
      permissions: nextCurrentUser?.permissions ?? [],
    });
  },
  bootstrap: async () => {
    get().hydrate();
    const accessToken = get().accessToken;

    if (!accessToken) {
      return undefined;
    }

    const currentUser = await getCurrentUserRequest(
      accessToken,
      get().userContext,
    );
    get().setCurrentUser(currentUser);
    return currentUser;
  },
  login: async (payload) => {
    const result = await loginRequest(payload);

    // 一部分账号需要先选组织/店铺，这里先缓存临时登录态。
    if (result.loginMode === 'select_context') {
      get().setPendingSelection(result.tempToken);
      return result;
    }

    const session = result.session;
    get().setSession(session);

    const currentUser =
      session.currentUser ||
      (await getCurrentUserRequest(session.accessToken, session.userContext));
    get().setCurrentUser(currentUser);

    return {
      ...result,
      session: {
        ...session,
        currentUser,
      },
    };
  },
  startSelectContextSwitch: async () => {
    const accessToken = get().accessToken;
    if (!accessToken) {
      throw new Error('登录状态已失效，请重新登录');
    }

    const result = await startSelectContextRequest(accessToken);
    setStoredTempToken(result.tempToken);
    set({
      tempToken: result.tempToken,
      hydrated: true,
    });

    return result.companies;
  },
  ensureSelectContextTempToken: async () => {
    get().hydrate();
    if (get().tempToken) {
      return get().tempToken;
    }

    if (!get().accessToken) {
      throw new Error('临时登录态已失效，请重新登录');
    }

    await get().startSelectContextSwitch();
    return get().tempToken;
  },
  loadSelectContextOptions: async () => {
    const tempToken = await get().ensureSelectContextTempToken();
    try {
      return await getSelectContextOptionsRequest(tempToken);
    } catch (error) {
      if (!get().accessToken) {
        throw error;
      }

      await get().startSelectContextSwitch();
      return getSelectContextOptionsRequest(get().tempToken);
    }
  },
  selectLoginContext: async (payload) => {
    const tempToken = await get().ensureSelectContextTempToken();
    let session: SessionPayload;

    try {
      session = await selectContextRequest(payload, tempToken);
    } catch (error) {
      if (!get().accessToken) {
        throw error;
      }

      await get().startSelectContextSwitch();
      session = await selectContextRequest(payload, get().tempToken);
    }

    get().setSession(session);

    const currentUser =
      session.currentUser ||
      (await getCurrentUserRequest(session.accessToken, session.userContext));
    get().setCurrentUser(currentUser);

    return currentUser;
  },
  logout: async () => {
    try {
      await logoutRequest(get().accessToken);
    } finally {
      get().clearSessionState();
    }
  },
  clearSessionState: () => {
    clearStoredTokens();
    set({
      ...resetState,
      hydrated: true,
    });
  },
}));
